import type { Payload } from '@hapi/boom';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { Method } from 'axios';
import type { AxiosRequestConfig, AxiosError } from 'axios';

interface AxiosBaseQuery {
  baseUrl?: string;
}

interface QueryArgs extends AxiosRequestConfig {
  url: string;
  method: Method;
}

export interface AxiosBaseQueryError {
  status?: number;
  data?: Payload; // All errors from node server should be Boomified
}

type AxiosBaseQueryResult = BaseQueryFn<QueryArgs, unknown, unknown>;

export function axiosBaseQuery(input: AxiosBaseQuery): AxiosBaseQueryResult {
  const baseUrl = input.baseUrl ?? '';

  return async (queryArgs) => {
    try {
      const url = `${baseUrl}${queryArgs.url}`;

      /*
       * CSRF ON EVERY REQUEST THROUGH THIS QUERY, NOT JUST THE ONES SOMEONE
       * REMEMBERED. The server enables csurf whenever `auth.csrf` is 'true' and
       * exempts only /arena and Bearer-token callers, so every cookie-auth
       * POST/PUT/DELETE from the browser needs the header. Apollo's link sets it
       * (client/index.tsx); this base query — which every ControlGen and
       * node-genai call goes through — did not.
       *
       * ⛔ THE FAILURE MODE IS ENVIRONMENT-SHAPED, WHICH IS WHY IT SURVIVED. With
       * `auth.csrf` off locally every write succeeds; turn it on in a deployed
       * environment and the same build returns 403 "Invalid CSRF token" from
       * buttons that were tested and worked. Measured on dev 2026-09-09:
       * attaching a bundle to a project failed exactly this way.
       *
       * GETs were never affected — csurf ignores safe methods — so the surface
       * reads correctly and only writes fail, which reads as a permissions
       * problem rather than a missing header.
       */
      const headers = {
        ...queryArgs.headers,
        ...(typeof window !== 'undefined' &&
          window.__CSRF_TOKEN__ && {
            'X-CSRF-Token': window.__CSRF_TOKEN__,
          }),
      };

      const result = await axios({
        ...queryArgs,
        url,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      // All errors from Node server should be in Boom format
      const err = axiosError as AxiosError<Payload>;
      const error: AxiosBaseQueryError = {
        status: err.response?.status,
        data: err.response?.data,
      };
      return {
        error,
        meta: {
          // Leave a hint, in case RTK error middleware wants to know
          // if the error comes from axios
          type: 'axios',
          request: err.config,
          response: err.response,
        },
      };
    }
  };
}
